import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import {
  upsertBusinessProfile,
  getVendorBusinessProfile,
  clearBusinessProfileState,
} from "../redux/slices/businessProfileSlice";

const ManageInvoiceDetail = () => {
  const dispatch = useDispatch();

  const { profile, loading, success, error } = useSelector(
    (state) => state.businessProfile
  );
  const { user } = useSelector((state) => state.auth);
  const vendorId = user?.vendor?._id;

  const [form, setForm] = useState({
    businessName: "",
    gstNumber: "",
    gstType: "GST",
    gstValue: 0,
    address: "",
    contactNumber: "",
    email: ""
  });

  const [logo, setLogo] = useState(null);
  const [preview, setPreview] = useState(null);

  /* ===============================
     FETCH PROFILE (EDIT MODE)
  ================================ */
  useEffect(() => {
    if (vendorId) {
      dispatch(getVendorBusinessProfile(vendorId));
    }
  }, [vendorId, dispatch]);

  /* ===============================
     PREFILL FORM
  ================================ */
  useEffect(() => {
    if (profile) {
      setForm({
        businessName: profile.businessName || "",
        gstNumber: profile.gstNumber || "",
        gstType: profile.gstType || "GST",
        gstValue: profile.gstValue || 0,
        address: profile.address || "",
        contactNumber: profile.contactNumber || "",
        email: profile.email
      });

      if (profile.logo?.url) {
        setPreview(profile.logo.url);
      }
    }
  }, [profile]);

  /* ===============================
     HANDLE CHANGE
  ================================ */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  /* ===============================
     HANDLE LOGO
  ================================ */
  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogo(file);
      setPreview(URL.createObjectURL(file));
    }
  };

  /* ===============================
     SUBMIT
  ================================ */
  const handleSubmit = (e) => {
    e.preventDefault();

    const formData = new FormData();
    formData.append("vendor", vendorId);
    formData.append("businessName", form.businessName);
    formData.append("gstNumber", form.gstNumber);
    formData.append("gstType", form.gstType);
    formData.append("gstValue", form.gstValue);
    formData.append("address", form.address);
    formData.append("contactNumber", form.contactNumber);
    formData.append("email", form.email);
    if (logo) {
      formData.append("logo", logo);
    }

    dispatch(upsertBusinessProfile(formData));
  };

  /* ===============================
     HANDLE SUCCESS / ERROR WITH SWEET ALERT
  ================================ */
  useEffect(() => {
    if (success) {
      Swal.fire({
        icon: "success",
        title: "Success",
        text: "Business profile saved successfully!",
        timer: 2000,
        showConfirmButton: false,
      });
      dispatch(clearBusinessProfileState());
    }

    if (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error,
        timer: 2500,
        showConfirmButton: false,
      });
    }
  }, [success, error, dispatch]);

  /* ===============================
     UI
  ================================ */
  return (
    <div className="container my-4">
      <div className="card shadow-sm">
        <div className="card-header bg-dark text-white">
          <h6 className="mb-0 text-white">Manage Invoice Detail</h6>
        </div>

        <div className="card-body">
          <form onSubmit={handleSubmit} encType="multipart/form-data">
            {/* LOGO */}
            <div className="mb-3">
              <label className="form-label">Business Logo</label>
              <input
                type="file"
                accept="image/*"
                className="form-control"
                onChange={handleLogoChange}
              />
              {preview && (
                <img
                  src={preview}
                  alt="logo"
                  className="img-thumbnail mt-2"
                  style={{ width: 120 }}
                />
              )}
            </div>

            {/* BUSINESS NAME */}
            <div className="mb-3">
              <label className="form-label">Business Name</label>
              <input
                type="text"
                name="businessName"
                className="form-control"
                value={form.businessName}
                onChange={handleChange}
                required
              />
            </div>

            {/* GST NUMBER */}
            <div className="mb-3">
              <label className="form-label">GST Number</label>
              <input
                type="text"
                name="gstNumber"
                className="form-control"
                value={form.gstNumber}
                onChange={handleChange}
              />
            </div>

            <div className="row">
              {/* GST TYPE */}
              <div className="col-md-6 mb-3">
                <label className="form-label">GST Type</label>
                <select
                  name="gstType"
                  className="form-select"
                  value={form.gstType}
                  onChange={handleChange}
                >
                  <option value="GST">GST</option>
                  <option value="CGST_SGST">CGST + SGST</option>
                  <option value="IGST">IGST</option>
                </select>
              </div>

              {/* GST VALUE */}
              <div className="col-md-6 mb-3">
                <label className="form-label">GST %</label>
                <select
                  name="gstValue"
                  className="form-select"
                  value={form.gstValue}
                  onChange={handleChange}
                >
                  <option value={0}>0%</option>
                  <option value={5}>5%</option>
                  <option value={9}>9%</option>
                  <option value={12}>12%</option>
                  <option value={18}>18%</option>
                  <option value={28}>28%</option>
                </select>
              </div>
            </div>

            {/* ADDRESS */}
            <div className="mb-3">
              <label className="form-label">Address</label>
              <textarea
                name="address"
                className="form-control"
                rows={3}
                value={form.address}
                onChange={handleChange}
              />
            </div>

            {/* CONTACT */}
            <div className="mb-3">
              <label className="form-label">Contact Number</label>
              <input
                type="text"
                name="contactNumber"
                className="form-control"
                value={form.contactNumber}
                onChange={handleChange}
              />
            </div>

            {/* EMAIL */}
            <div className="mb-3">
              <label className="form-label">Email</label>
              <input
                type="email"
                name="email"
                className="form-control"
                value={form.email}
                onChange={handleChange}
              />
            </div>

            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {profile ? "Update Detail" : "Save Detail"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ManageInvoiceDetail;
